import React from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { Mail, Globe } from 'lucide-react';

const olympiads = [
  'Mathematics',
  'Chemistry',
  'Physics',
  'Computing & AI',
  'Science',
  'Biology',
  'Astronomy',
];

export default function Footer() {
  return (
    <footer className="bg-foreground text-primary-foreground">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-10">
          <div className="xl:col-span-2">
            <div className="flex items-center gap-2.5 mb-4">
              <AppLogo size={36} />
              <span className="font-extrabold text-lg text-white tracking-tight">
                OnlineOlympiads<span className="text-secondary">.org</span>
              </span>
            </div>
            <p className="text-sm text-slate-400 leading-relaxed max-w-xs">
              A global future-skills and olympiad platform helping students benchmark their analytical, scientific, and computational abilities through international assessments.
            </p>
            <div className="flex items-center gap-3 mt-6">
              <a href="mailto:hello@onlineolympiads.org" className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-secondary transition-colors">
                <Mail size={14} />
                hello@onlineolympiads.org
              </a>
            </div>
            <div className="flex gap-3 mt-4">
              <a href="#" className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center hover:bg-secondary/30 transition-colors">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="text-slate-300"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.259 5.63 5.905-5.63zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              </a>
              <a href="#" className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center hover:bg-secondary/30 transition-colors">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="text-slate-300"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              </a>
              {/* Instagram */}
              <a href="https://www.instagram.com" target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center hover:bg-secondary/30 transition-colors" aria-label="Follow us on Instagram">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="text-slate-300">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
              <a href="#" className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center hover:bg-secondary/30 transition-colors">
                <Globe size={14} className="text-slate-300" />
              </a>
            </div>
          </div>

          <div>
            <h4 className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-4">Olympiads</h4>
            <ul className="flex flex-col gap-2.5">
              {olympiads?.map((o) => (
                <li key={`footer-olympiad-${o}`}>
                  <Link href="/individual-olympiad-page" className="text-sm text-slate-400 hover:text-secondary transition-colors">
                    {o}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-4">Platform</h4>
            <ul className="flex flex-col gap-2.5">
              {[
                { label: 'Student Dashboard', href: '/student-dashboard' },
                { label: 'Take an Exam', href: '/online-exam-interface' },
                { label: 'Report Cards', href: '/report-card-certificate-page' },
                { label: 'School Partnerships', href: '/schools' },
                { label: 'Leaderboards', href: '/leaderboard' },
                { label: 'Verify Certificate', href: '/verify/FM-2026-MATH-001234' },
              ]?.map((item) => (
                <li key={`footer-platform-${item?.label}`}>
                  <Link href={item?.href} className="text-sm text-slate-400 hover:text-secondary transition-colors">
                    {item?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-4">Company</h4>
            <ul className="flex flex-col gap-2.5">
              {[
                { label: 'About Us', href: '/about' },
                { label: 'Blog & Resources', href: '/blogs' },
                { label: 'Contact', href: '/contact' },
                { label: 'School Partnerships', href: '/schools' },
                { label: 'Privacy Policy', href: '/privacy-policy' },
                { label: 'Terms of Service', href: '/terms' },
              ]?.map((item) => (
                <li key={`footer-company-${item?.label}`}>
                  <Link href={item?.href} className="text-sm text-slate-400 hover:text-secondary transition-colors">
                    {item?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-slate-500">
            © 2026 OnlineOlympiads.org. All rights reserved. Empowering the next generation of thinkers.
          </p>
          <div className="flex items-center gap-4">
            <span className="text-xs text-slate-500">Recognized globally in 40+ countries</span>
            <div className="flex gap-2">
              <span className="px-2 py-1 text-xs bg-white/10 rounded text-slate-400">ISO Certified</span>
              <span className="px-2 py-1 text-xs bg-white/10 rounded text-slate-400">SSL Secured</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}